import { Fragment, type ReactNode } from "react";

import { KebabMenu } from "@/components/KebabMenu";
import { ErrorBox, type LoadFailure } from "@/components/Spinner";

import { Button } from "./Button";
import type { NoticeTone } from "./Notice";
import { Skeleton } from "./Skeleton";

/**
 * THE table. One header, one row height, one hover, one border and one radius
 * for every list of the console, whether it sits alone on a page or inside a
 * card (it draws its own frame, so it never needs one around it). A page says
 * WHAT is in it — the columns, the rows, the words — and the table owns HOW:
 *
 *   header    on `sunken`, 12 px semibold, sentence case, `scope="col"`, one name
 *             for a screen reader (`label`)
 *   rows      56 px at least, a `line` separator, a hover on EVERY row (`ground`, or
 *             one step stronger on a tinted row)
 *   actions   ALWAYS one row menu (the ellipsis button) in the last column (`actions`); the destructive
 *             entry is a `<MenuItem danger>` that opens the `ConfirmModal`. A row
 *             that has none draws nothing, and its cell stays aligned
 *   states    `rows` is `undefined` while the data is on its way: the header
 *             stays and the body is drawn as rows of bars (the same height as
 *             the real ones, so nothing jumps). `error` (a load that failed with
 *             nothing to show) and `empty` (an `EmptyState`) take the body's
 *             place the same way. The frame never disappears.
 *
 * Deliberately absent: sorting, selection, column resizing, virtualisation.
 * None of the console's lists has them; a page that needs one adds it here for
 * every table at once, never by drawing its own.
 *
 * A row is not a link. Whatever the row leads to is a real `<a>` in its first
 * cell (a keyboard and a screen reader reach it; an `onClick` on a `<tr>` is
 * reached by neither), and the other things one can do with it are in `actions`.
 */

/** A message tone a row or a cell may be tinted with (one that needs a look). */
type TableTint = NoticeTone;

export interface TableColumn<Row> {
  /** Stable id of the column (a React key). */
  key: string;
  /** The header, in sentence case ("Last event", not "LAST EVENT"). */
  header: string;
  /** What the cell shows. Content only: the cell's padding, size and colour are the table's. */
  cell: (row: Row) => ReactNode;
  /** `right` for figures and ages: the header follows. */
  align?: "left" | "right";
  /** Never wrap: a date, an identifier. */
  nowrap?: boolean;
  /** Secondary information (`ink-muted`). */
  muted?: boolean;
  /** An identifier or a figure: monospace, small. */
  mono?: boolean;
  /** A column that holds a paragraph (14 rem at least). */
  wide?: boolean;
  /** Tint this row's cell in this column: a state that needs a look. */
  tint?: (row: Row) => TableTint | undefined;
}

interface TableProps<Row> {
  /** What the table lists ("Tenants"): its name for a screen reader, and the noun of "Could not load tenants". */
  label: string;
  columns: TableColumn<Row>[];
  /** The rows; `undefined` while the data has not arrived. */
  rows: Row[] | undefined;
  /**
   * A stable key per row. `index` is there for a row that has no identity of its own (two runs
   * without a name): a key that repeats is made unique by the table, never left to collide.
   */
  rowKey: (row: Row, index: number) => string;
  /** Shown in the body when there are no rows: an `EmptyState`. */
  empty: ReactNode;
  /** A load that failed with nothing to show (`loadFailure(query)`). */
  error?: LoadFailure;
  /** The row menu of each row (see above). `items` is `null` for a row with no action. */
  actions?: {
    /** Names the button for a screen reader: "Actions for <the row>". */
    label: (row: Row) => string;
    /** `MenuItem`s (and `MenuDivider`s); `null` when this row has none. */
    items: (row: Row) => ReactNode;
  };
  /** A full-width row shown under `row` while it returns content. The page holds the "open" state. */
  detail?: (row: Row) => ReactNode;
  /**
   * `muted` sets a row that is no longer live (a closed item) on the `sunken` fill with muted text — both
   * measured to keep 4.5:1 for its text AND its links, where a faded row (opacity) fell to 2.2:1; a tone
   * tints a row that needs a look.
   */
  rowEmphasis?: (row: Row) => "muted" | TableTint | undefined;
  /** A page of more rows exists: draws "Load more" under the table. */
  more?: { loading: boolean; onLoadMore: () => void };
}

// Literal tables, not `bg-${tone}-soft`: Tailwind finds a class by reading its
// full text in the source.
// The soft fill of a tone is also half of `TONE_SURFACE` (Notice.tsx: fill + edge, for a box). It is NOT folded into it on
// purpose: a cell has no border of its own, and a row's tints (`ROW_TINT`: 40 % at rest, the full fill on hover) are not a
// box's — one shared table would make either of them carry what it does not use.
const CELL_TINT = {
  info: "bg-info-soft",
  success: "bg-success-soft",
  warning: "bg-warning-soft",
  danger: "bg-danger-soft",
} as const satisfies Record<TableTint, string>;

// A tinted row keeps a hover: one step stronger than its resting tint.
const ROW_TINT = {
  info: "bg-info-soft/40 hover:bg-info-soft",
  success: "bg-success-soft/40 hover:bg-success-soft",
  warning: "bg-warning-soft/40 hover:bg-warning-soft",
  danger: "bg-danger-soft/40 hover:bg-danger-soft",
} as const satisfies Record<TableTint, string>;

const SKELETON_ROWS = 5;
// A different width per column so the bars read as text, not as a grid.
const BAR_WIDTHS = ["w-2/3", "w-1/2", "w-3/4", "w-1/3", "w-1/2", "w-2/3"] as const;

function lowerFirst(text: string): string {
  // "Tenants" → "tenants"; an acronym ("GUI builds") keeps its capitals.
  return /^[A-Z][a-z]/.test(text) ? text.charAt(0).toLowerCase() + text.slice(1) : text;
}

function cellClass<Row>(column: TableColumn<Row>, row: Row, muted: boolean): string {
  const tint = column.tint?.(row);
  return [
    "px-4 py-3 align-middle",
    column.mono ? "font-mono text-xs" : "text-sm",
    column.muted || muted ? "text-ink-muted" : "text-ink",
    column.align === "right" ? "text-right" : "text-left",
    column.nowrap ? "whitespace-nowrap" : "",
    column.wide ? "min-w-[14rem]" : "",
    tint ? CELL_TINT[tint] : "",
  ]
    .filter(Boolean)
    .join(" ");
}

function rowClass(emphasis: "muted" | TableTint | undefined): string {
  const base = "h-14 border-t border-line first:border-t-0";
  if (emphasis === "muted") return `${base} bg-sunken hover:bg-sunken-deep`;
  if (emphasis) return `${base} ${ROW_TINT[emphasis]}`;
  return `${base} hover:bg-ground`;
}

/** The rows with a key each; a key that repeats gets a suffix, so two rows never share one. */
function keyed<Row>(rows: Row[], rowKey: (row: Row, index: number) => string): { row: Row; key: string }[] {
  const seen = new Map<string, number>();
  return rows.map((row, index) => {
    const key = rowKey(row, index);
    const times = seen.get(key) ?? 0;
    seen.set(key, times + 1);
    return { row, key: times === 0 ? key : `${key}~${times}` };
  });
}

export function Table<Row>({
  label,
  columns,
  rows,
  rowKey,
  empty,
  error,
  actions,
  detail,
  rowEmphasis,
  more,
}: TableProps<Row>) {
  const span = columns.length + (actions ? 1 : 0);
  const loading = rows === undefined && !error;

  return (
    // ``relative``: the ``sr-only`` texts inside (the actions header, the loading status) are
    // absolutely positioned, and without a containing block of their own they sit where the table
    // overflows — outside this frame, where they stretch the PAGE sideways.
    <div className="relative overflow-x-auto rounded-lg border border-line bg-surface" aria-busy={loading}>
      <table aria-label={label} className="min-w-full">
        <thead>
          <tr className="border-b border-line bg-sunken">
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={`whitespace-nowrap px-4 py-2.5 text-xs font-semibold text-ink-muted ${
                  column.align === "right" ? "text-right" : "text-left"
                }`}
              >
                {column.header}
              </th>
            ))}
            {actions && (
              <th scope="col" className="w-px px-4 py-2.5">
                <span className="sr-only">Actions</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {error ? (
            <tr>
              <td colSpan={span} className="p-4">
                <ErrorBox
                  error={error.error}
                  title={`Could not load ${lowerFirst(label)}`}
                  onRetry={error.onRetry}
                  retrying={error.retrying}
                />
              </td>
            </tr>
          ) : rows === undefined ? (
            Array.from({ length: SKELETON_ROWS }, (_, index) => (
              <tr key={index} aria-hidden="true" className="h-14 border-t border-line first:border-t-0">
                {columns.map((column, position) => (
                  <td key={column.key} className="px-4 py-3">
                    <Skeleton className={`h-4 ${BAR_WIDTHS[(position + index) % BAR_WIDTHS.length]}`} />
                  </td>
                ))}
                {actions && <td className="px-4 py-3" />}
              </tr>
            ))
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={span}>{empty}</td>
            </tr>
          ) : (
            keyed(rows, rowKey).map(({ row, key }) => {
              const open = detail?.(row);
              const menu = actions?.items(row);
              const emphasis = rowEmphasis?.(row);
              return (
                <Fragment key={key}>
                  <tr className={rowClass(emphasis)}>
                    {columns.map((column) => (
                      <td key={column.key} className={cellClass(column, row, emphasis === "muted")}>
                        {column.cell(row)}
                      </td>
                    ))}
                    {actions && (
                      <td className="w-px whitespace-nowrap px-4 py-3 align-middle">
                        {menu ? (
                          <div className="flex justify-end">
                            <KebabMenu label={actions.label(row)} variant="ghost" size="sm">
                              {menu}
                            </KebabMenu>
                          </div>
                        ) : null}
                      </td>
                    )}
                  </tr>
                  {open ? (
                    <tr>
                      <td colSpan={span} className="bg-sunken px-4 py-4 text-sm text-ink">
                        {open}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })
          )}
        </tbody>
      </table>
      {loading && <span role="status" className="sr-only">Loading {lowerFirst(label)}…</span>}
      {more && (
        <div className="border-t border-line px-4 py-3 text-center">
          <Button variant="secondary" loading={more.loading} onClick={more.onLoadMore}>
            {more.loading ? "Loading…" : "Load more"}
          </Button>
        </div>
      )}
    </div>
  );
}
